<script setup lang="ts">
import { Mail, Phone, ShieldCheck, Star, UserRound } from "lucide-vue-next";
import type {
  PublicWorkerProfile,
  PublicWorkerReviewSummary,
} from "~/types/public-worker";
import { toTitleCase } from "~/utils/search-catalog";
import { formatPersonName } from "~/utils/worker-profile";

const {
  worker,
  reviewSummary,
  summaryOnly = false,
} = defineProps<{
  worker: PublicWorkerProfile;
  reviewSummary: PublicWorkerReviewSummary;
  summaryOnly?: boolean;
}>();

const { t } = useI18n();
const profileImageBroken = ref(false);

const firstName = computed(() => formatPersonName(worker.name));
const fullName = computed(() => formatPersonName(worker.name, worker.lastname));
const profileImageSrc = computed(() => worker.profileImageUrl?.trim() || "");
const hasProfileImage = computed(
  () => Boolean(profileImageSrc.value) && !profileImageBroken.value,
);

const categoryName = computed(() => {
  const value = worker.primaryCategoryName;
  if (!value) return t("worker_profile.professional");

  const key = `professional_category.${value}`;
  const translated = t(key);

  if (translated !== key) return translated;

  const categoryKey = `category.${value}`;
  const category = t(categoryKey);
  return category === categoryKey ? toTitleCase(value) : category;
});

const regionName = computed(() => {
  if (worker.coverageType === "nationwide") {
    return t("worker_profile.nationwide");
  }

  const value = worker.primaryRegionName;
  if (!value) return "";

  const key = `region.${value}`;
  const translated = t(key);
  return translated === key ? toTitleCase(value) : translated;
});

const professionalHeadline = computed(() => {
  if (worker.coverageType === "nationwide") {
    return t("worker_profile.professional_nationwide", {
      category: categoryName.value,
    });
  }

  return regionName.value
    ? t("worker_profile.professional_in_region", {
        category: categoryName.value,
        region: regionName.value,
      })
    : categoryName.value;
});
</script>

<template>
  <aside
    class="overflow-hidden border border-app-border bg-white shadow-sm"
    :class="
      summaryOnly ? 'rounded-2xl md:mx-auto md:max-w-[780px]' : 'rounded-xl'
    "
  >
    <template v-if="summaryOnly">
      <div
        class="min-w-0 md:grid md:min-h-[260px] md:grid-cols-[280px_minmax(0,1fr)]"
      >
        <div class="relative min-w-0">
          <div
            class="aspect-[4/3] w-full overflow-hidden bg-primary-50 md:h-full md:min-h-[260px] md:aspect-auto"
          >
            <div
              class="flex size-full items-center justify-center text-5xl font-bold"
            >
              <img
                v-if="hasProfileImage"
                :src="profileImageSrc"
                :alt="fullName"
                class="size-full object-cover object-center"
                @error="profileImageBroken = true"
              />

              <UserRound
                v-else
                class="size-24 text-primary md:size-28"
                :stroke-width="1.35"
                aria-hidden="true"
              />
            </div>
          </div>

          <a
            href="#reviews"
            class="absolute bottom-3 left-1/2 z-10 inline-flex h-9 -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-app-border bg-white px-3.5 text-xs font-semibold text-app-text shadow-md transition hover:border-primary/30 hover:text-primary"
          >
            <Star class="size-3.5 shrink-0 fill-amber-400 text-amber-400" />

            <template v-if="reviewSummary.count">
              <span>{{ reviewSummary.average.toFixed(1) }}</span>
              <span class="font-medium text-app-muted">
                ({{ reviewSummary.count }})
              </span>
            </template>

            <span v-else class="font-medium text-app-muted">
              {{ $t("worker_profile.no_reviews_short") }}
            </span>
          </a>
        </div>

        <div
          class="flex min-w-0 flex-col justify-center px-5 py-5 text-center sm:px-6 sm:py-6 md:items-start md:px-8 md:py-7 md:text-left"
        >
          <p
            class="break-words text-xl font-bold leading-tight text-app-text sm:text-2xl"
          >
            {{ fullName }}
          </p>

          <p class="mt-1 break-words text-sm font-medium text-app-muted">
            {{ professionalHeadline }}
          </p>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="p-5 pb-4">
        <div
          class="relative mx-auto aspect-square w-full max-w-[220px] overflow-hidden rounded-2xl bg-primary-50 ring-1 ring-primary/10"
        >
          <div
            class="flex size-full items-center justify-center text-4xl font-bold"
          >
            <img
              v-if="hasProfileImage"
              :src="profileImageSrc"
              :alt="fullName"
              class="size-full object-cover object-center transition duration-300 hover:scale-[1.025]"
              @error="profileImageBroken = true"
            />

            <UserRound
              v-else
              class="size-24 text-primary"
              :stroke-width="1.35"
              aria-hidden="true"
            />
          </div>

          <a
            href="#reviews"
            class="absolute bottom-3 left-1/2 z-10 inline-flex h-9 -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-app-border bg-white px-3.5 text-xs font-semibold text-app-text shadow-md transition hover:border-primary/30 hover:text-primary"
          >
            <Star class="size-3.5 shrink-0 fill-amber-400 text-amber-400" />

            <template v-if="reviewSummary.count">
              <span>{{ reviewSummary.average.toFixed(1) }}</span>
              <span class="font-medium text-app-muted">
                ({{ reviewSummary.count }})
              </span>
            </template>

            <span v-else class="font-medium text-app-muted">
              {{ $t("worker_profile.no_reviews_short") }}
            </span>
          </a>
        </div>
      </div>

      <div class="border-t border-app-border p-5">
        <p
          class="text-xs font-semibold uppercase tracking-[0.1em] text-primary"
        >
          {{ $t("worker_profile.contact_professional") }}
        </p>

        <h2 class="mt-1 text-2xl font-bold text-app-text">
          {{
            $t("worker_profile.contact_professional_name", { name: firstName })
          }}
        </h2>

        <p class="mt-2 text-sm leading-6 text-app-muted">
          {{ $t("worker_profile.contact_professional_description") }}
        </p>

        <div class="mt-5 space-y-2.5">
          <a
            v-if="worker.contactPhone"
            :href="`tel:${worker.contactPhone}`"
            class="flex h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-400"
          >
            <Phone class="size-4" />
            {{ $t("worker_profile.call") }}
          </a>

          <a
            v-if="worker.contactEmail"
            :href="`mailto:${worker.contactEmail}`"
            class="flex h-11 items-center justify-center gap-2 rounded-lg border border-primary bg-white px-4 text-sm font-semibold text-primary transition hover:bg-primary-50"
          >
            <Mail class="size-4" />
            {{ $t("worker_profile.email") }}
          </a>
        </div>

        <div class="mt-4 flex items-start gap-3 rounded-lg bg-primary-50 p-3">
          <ShieldCheck class="mt-0.5 size-4 shrink-0 text-primary" />

          <p class="text-xs leading-5 text-app-muted">
            {{ $t("worker_profile.contact_safety_hint", { name: firstName }) }}
          </p>
        </div>
      </div>
    </template>
  </aside>
</template>
